<template>
  <div class="fts-settings-page">
    <header class="fts-settings-head">
      <div>
        <h1>Appearance</h1>
        <p>How Inistate looks on this device.</p>
      </div>
    </header>

    <section class="fts-settings-card">
      <div>
        <h2>Theme</h2>
        <p class="fts-settings-card-sub">
          Saved in this browser only — another device keeps its own.
        </p>
      </div>

      <div class="fts-settings-row">
        <div class="fts-settings-row-text">
          <strong>Match my system</strong>
          <span>Switch between light and dark when your computer does.</span>
        </div>
        <fts-toggle-switch v-model="syncOS" />
      </div>

      <div class="fts-settings-seg" role="radiogroup" aria-label="Theme">
        <button
          type="button"
          v-for="option in themes"
          :key="option.id"
          role="radio"
          :aria-checked="String(theme === option.id)"
          :class="{ 'is-on': theme === option.id }"
          :disabled="syncOS"
          @click="setTheme(option.id)"
        >
          {{ option.label }}
        </button>
      </div>
      <p class="fts-settings-note" v-if="syncOS">
        Following your system, which is set to {{ theme }} right now.
      </p>
    </section>

    <!-- reading (FTR00375): this person's own text size and contrast, kept on this device -->
    <section class="fts-settings-card fts-settings-reading" v-if="readingShown">
      <div>
        <h2>Reading</h2>
        <p class="fts-settings-card-sub">
          Bigger, darker form text, for you only. Saved in this browser only — other people who
          sign in here, and your other devices, keep their own.
        </p>
      </div>

      <div class="fts-settings-row-text">
        <strong id="reading-size-label">Text size</strong>
        <span>{{ readingSizeNote }}</span>
      </div>
      <div class="fts-settings-seg" role="radiogroup" aria-labelledby="reading-size-label">
        <button
          type="button"
          v-for="option in readingSizes"
          :key="option.id"
          role="radio"
          :aria-checked="String(reading.textSize === option.id)"
          :class="{ 'is-on': reading.textSize === option.id }"
          @click="setReading({ textSize: option.id })"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="fts-settings-row-text">
        <strong id="reading-contrast-label">Contrast</strong>
        <span>{{ readingContrastNote }}</span>
      </div>
      <div class="fts-settings-seg" role="radiogroup" aria-labelledby="reading-contrast-label">
        <button
          type="button"
          v-for="option in readingContrasts"
          :key="option.id"
          role="radio"
          :aria-checked="String(reading.contrast === option.id)"
          :class="{ 'is-on': reading.contrast === option.id }"
          @click="setReading({ contrast: option.id })"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="fts-settings-row">
        <div class="fts-settings-row-text">
          <strong>Follow my device's contrast setting</strong>
          <span>When your computer or phone asks apps for more contrast, use High.</span>
        </div>
        <fts-toggle-switch v-model="readingFollowDevice" />
      </div>
    </section>

    <section class="fts-settings-card">
      <div>
        <h2>Colours</h2>
        <p class="fts-settings-card-sub">Applies straight away, so you can see it before you choose.</p>
      </div>

      <div class="fts-settings-seg" role="radiogroup" aria-label="Colours">
        <button
          type="button"
          v-for="option in colours"
          :key="option.id"
          role="radio"
          :aria-checked="String(colour === option.id)"
          :class="{ 'is-on': colour === option.id }"
          @click="setColour(option.id)"
        >
          {{ option.name }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * Settings › Your account › Appearance — the old profile modal's Preferences tab.
 *
 * These have always been browser preferences, not account settings: the three localStorage keys
 * below are read by the app shell at start-up and nothing is sent to the server. The page says so
 * ("on this device") because it sits in the account group, where everything else DOES follow you
 * to another device, and without that line someone would reasonably expect this to as well.
 *
 * The keys, the body class and the colour classes are the old tab's exactly, so a choice made in
 * either place is the same choice.
 *
 * Reading (FTR00375, docs/frontend-accessibility-text-contrast-proposal.md in the SVN repo) is the
 * same kind of setting, kept per signed-in person: components/commons/readability.js stores it,
 * paints it on <html>, and is what the old dialog's Reading block uses too.
 */
import FTSToggleSwitch from "../../elements/FTSToggleSwitch";
import { authorizationStore } from "../../store/authorizationStore";
import { clientStore } from "../../store/clientStore";
import { READING_STAGE, readDisplay, writeDisplay, applyDisplay, isCustomised } from "../../commons/readability";
import { stageAllows } from "../settingsStage";

const READING_SIZES = [
  { id: "default", label: "Default", note: "Default is the standard size everyone sees." },
  { id: "large", label: "Large", note: "Form labels and values at 16 px, on every screen width." },
  { id: "larger", label: "Larger", note: "Form labels and values at 18 px, on every screen width." },
];
const READING_CONTRASTS = [
  { id: "default", label: "Default", note: "The colours everyone sees." },
  { id: "high", label: "High", note: "Darker text and greys, visible field borders, a thick focus ring." },
];

const THEME_KEY = "fetias-client-theme";
const SYNC_KEY = "fetias-client-theme-sync-os";
const COLOUR_KEY = "fetias-client-color";

const COLOURS = [
  { id: "fts-color--default", name: "Default" },
  { id: "fts-color--client", name: "Client" },
  { id: "fts-color--studio", name: "Studio" },
  { id: "fts-color--immersive", name: "Immersive" },
  { id: "fts-color--violet", name: "Violet" },
];

function read(key) {
  try { return window.localStorage.getItem(key); } catch (e) { return null; }
}

function write(key, value) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (e) { /* private window: the choice still applies to this visit */ }
}

export default {
  name: "SettingsAppearance",
  components: { "fts-toggle-switch": FTSToggleSwitch },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      theme: read(THEME_KEY) === "dark" ? "dark" : "light",
      sync: !!read(SYNC_KEY),
      colour: COLOURS.some(c => c.id === read(COLOUR_KEY)) ? read(COLOUR_KEY) : COLOURS[0].id,
      themes: [{ id: "light", label: "Light" }, { id: "dark", label: "Dark" }],
      colours: COLOURS,
      media: null,
      reading: readDisplay(authorizationStore.getters.userName),
      readingSizes: READING_SIZES,
      readingContrasts: READING_CONTRASTS,
    };
  },
  computed: {
    /* on the release ladder, or already changed: a choice that is applied can always be undone */
    readingShown() {
      return stageAllows(READING_STAGE, {
        enabled: true,
        internal: authorizationStore.getters.isInternal,
        beta: authorizationStore.getters.isBeta,
        workspaceBeta: clientStore.getters.workspaceBeta,
      }) || isCustomised(this.reading);
    },
    readingSizeNote() {
      const size = READING_SIZES.find(x => x.id === this.reading.textSize);
      return size ? size.note : "";
    },
    readingContrastNote() {
      const contrast = READING_CONTRASTS.find(x => x.id === this.reading.contrast);
      return contrast ? contrast.note : "";
    },
    readingFollowDevice: {
      get() {
        return this.reading.followDevice;
      },
      set(value) {
        this.setReading({ followDevice: Boolean(value) });
      },
    },
    syncOS: {
      get() {
        return this.sync;
      },
      set(value) {
        this.sync = !!value;
        write(SYNC_KEY, value ? "true" : null);
        if (value) this.applyTheme(this.systemTheme());
      },
    },
  },
  methods: {
    systemTheme() {
      return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    },
    setTheme(value) {
      if (this.sync) return;
      this.applyTheme(value);
    },
    applyTheme(value) {
      this.theme = value;
      write(THEME_KEY, value);
      document.body.classList.toggle("dark", value === "dark");
    },
    setColour(value) {
      this.colour = value;
      write(COLOUR_KEY, value);
      // The shell carries the colour class; outside it (a test, a harness) there is nothing to
      // repaint, and the stored choice still applies on the next load.
      const shell = document.body.getElementsByClassName("fts-client")[0];
      if (!shell) return;
      COLOURS.forEach(c => shell.classList.remove(c.id));
      shell.classList.add(value);
    },
    onSystemChange(event) {
      if (this.sync) this.applyTheme(event.matches ? "dark" : "light");
    },
    /* reading: save on this device for this person, then repaint at once */
    setReading(change) {
      const userName = authorizationStore.getters.userName;
      this.reading = writeDisplay(userName, Object.assign({}, this.reading, change));
      applyDisplay(userName);
    },
  },
  mounted() {
    if (window.matchMedia) {
      this.media = window.matchMedia("(prefers-color-scheme: dark)");
      if (this.media.addEventListener) this.media.addEventListener("change", this.onSystemChange);
    }
  },
  beforeDestroy() {
    if (this.media && this.media.removeEventListener) {
      this.media.removeEventListener("change", this.onSystemChange);
    }
  },
};
</script>
